import React from "react";
import { Lock, Clock, ShieldAlert, AlertOctagon, ArrowDownToLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { parseUtc } from '../../lib/datetime';

/**
 * ProvenanceBadge — one-line plain-language explanation of *why* a setting
 * is in its current state. Replaces technical governance jargon entirely.
 *
 * The backend's cascade resolver returns:
 *   { source, locked, lock_provenance, scheduled, break_glass_active, ... }
 *
 * We translate that into one of a small set of operator-language badges,
 * fully trilingual (EN/FR/ES). No raw IDs, no "cascade override conflict".
 */
export const ProvenanceBadge = ({ resolution }) => {
    const { t } = useTranslation();
    if (!resolution) return null;

    if (resolution.break_glass_active) {
        return (
            <Badge tone="warning" icon={AlertOctagon} testId="ob-prov-break-glass">
                {t("ob.governance.break_glass_until", { until: shortTime(resolution.break_glass_expires_at) })}
            </Badge>
        );
    }
    if (resolution.scheduled) {
        return (
            <Badge tone="info" icon={Clock} testId="ob-prov-scheduled">
                {resolution.scheduled_until
                    ? t("ob.governance.scheduled_until", { until: shortTime(resolution.scheduled_until) })
                    : t("ob.governance.scheduled_active")}
            </Badge>
        );
    }
    if (resolution.locked) {
        const tone = resolution.lock_provenance === "safety" ? "critical"
            : resolution.lock_provenance === "regulatory" ? "warning"
                : "muted";
        const Icon = resolution.lock_provenance === "safety" ? ShieldAlert : Lock;
        return (
            <Badge tone={tone} icon={Icon} testId="ob-prov-locked">
                {t(`ob.governance.lock_${resolution.lock_provenance || "operational"}`,
                    { source: resolution.source_org_name || t("ob.governance.governance") })}
            </Badge>
        );
    }
    if (resolution.source === "local") {
        return (
            <Badge tone="muted" testId="ob-prov-local">
                {t("ob.governance.source_local", { name: resolution.source_org_name || "" })}
            </Badge>
        );
    }
    if (resolution.source === "corporate" || resolution.source === "regional" || resolution.source === "inherited") {
        return (
            <Badge tone="muted" icon={ArrowDownToLine} testId="ob-prov-inherited">
                {t("ob.governance.source_inherited", { name: resolution.source_org_name || "" })}
            </Badge>
        );
    }
    return (
        <Badge tone="muted" testId="ob-prov-default">
            {t("ob.governance.source_default")}
        </Badge>
    );
};

const Badge = ({ children, tone = "muted", icon: Icon, testId }) => {
    const palette = {
        muted: { bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.08)", color: "var(--ob-text-secondary)" },
        info: { bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.25)", color: "#93C5FD" },
        warning: { bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.25)", color: "#93C5FD" },
        critical: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.30)", color: "#FCA5A5" },
    }[tone] || {};
    return (
        <span
            data-testid={testId}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
            style={{
                background: palette.bg,
                border: `1px solid ${palette.border}`,
                color: palette.color,
            }}
        >
            {Icon && <Icon size={12} strokeWidth={1.5} aria-hidden="true" />}
            <span className="truncate max-w-[260px]">{children}</span>
        </span>
    );
};

function shortTime(iso) {
    if (!iso) return "";
    try {
        const d = parseUtc(iso);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        return sameDay
            ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
            : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch (_) {
        return iso;
    }
}
